import React, { createContext, useContext, useState } from "react";

// Global settings to determine whether or not to show onboarding tips & modals
type OnboardingSettings = {
  tourActive: boolean;
  setTourActive: (b: boolean) => void;
  showSimulateTips: boolean;
  setShowSimulateTips: (b: boolean) => void;
  showBeforeAfterTips: boolean;
  setShowBeforeAfterTips: (b: boolean) => void;
  showConsultTips: boolean;
  setShowConsultTips: (b: boolean) => void;
  showDeployWidgetTips: boolean;
  setShowDeployWidgetTips: (b: boolean) => void;
  hasSeenWidgetDeployNudge: (clinicId?: string | null) => boolean;
  markWidgetDeployNudgeSeen: (clinicId?: string | null) => void;
  hasSeenDeployWidgetOnboarding: (clinicId?: string | null) => boolean;
  markDeployWidgetOnboardingSeen: (clinicId?: string | null) => void;
};

const OnboardingSettingsContext = createContext<OnboardingSettings>(
  {} as OnboardingSettings,
);

export const OnboardingSettingsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [tourActive, setTourActive] = useState(false);
  const [showSimulateTips, setShowSimulateTips] = useState(false);
  const [showBeforeAfterTips, setShowBeforeAfterTips] = useState(false);
  const [showConsultTips, setShowConsultTips] = useState(false);
  const [showDeployWidgetTips, setShowDeployWidgetTips] = useState(false);
  const [widgetDeployNudgeSeenByClinic, setWidgetDeployNudgeSeenByClinic] =
    useState<Record<string, boolean>>({});
  const [
    deployWidgetOnboardingSeenByClinic,
    setDeployWidgetOnboardingSeenByClinic,
  ] = useState<Record<string, boolean>>({});

  const getWidgetDeployNudgeSeenKey = (clinicId: string) =>
    `widget_deploy_nudge_seen_${clinicId}`;
  const getDeployWidgetOnboardingSeenKey = (clinicId: string) =>
    `deploy_widget_onboarding_seen_${clinicId}`;

  const getStoredBoolean = (key: string): boolean => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(key) === "true";
  };

  const hasSeenWidgetDeployNudge = (clinicId?: string | null): boolean => {
    if (!clinicId) return false;
    const fromState = widgetDeployNudgeSeenByClinic[clinicId];
    if (typeof fromState === "boolean") return fromState;
    return getStoredBoolean(getWidgetDeployNudgeSeenKey(clinicId));
  };

  const markWidgetDeployNudgeSeen = (clinicId?: string | null) => {
    if (!clinicId) return;

    setWidgetDeployNudgeSeenByClinic((prev) => ({
      ...prev,
      [clinicId]: true,
    }));

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        getWidgetDeployNudgeSeenKey(clinicId),
        "true",
      );
    }
  };

  const hasSeenDeployWidgetOnboarding = (clinicId?: string | null): boolean => {
    if (!clinicId) return false;
    const fromState = deployWidgetOnboardingSeenByClinic[clinicId];
    if (typeof fromState === "boolean") return fromState;
    return getStoredBoolean(getDeployWidgetOnboardingSeenKey(clinicId));
  };

  const markDeployWidgetOnboardingSeen = (clinicId?: string | null) => {
    if (!clinicId) return;

    setDeployWidgetOnboardingSeenByClinic((prev) => ({
      ...prev,
      [clinicId]: true,
    }));

    if (typeof window !== "undefined") {
      window.localStorage.setItem(
        getDeployWidgetOnboardingSeenKey(clinicId),
        "true",
      );
    }
  };

  return (
    <OnboardingSettingsContext.Provider
      value={{
        tourActive,
        setTourActive,

        showSimulateTips,
        setShowSimulateTips,

        showBeforeAfterTips,
        setShowBeforeAfterTips,

        showConsultTips,
        setShowConsultTips,

        showDeployWidgetTips,
        setShowDeployWidgetTips,
        hasSeenWidgetDeployNudge,
        markWidgetDeployNudgeSeen,
        hasSeenDeployWidgetOnboarding,
        markDeployWidgetOnboardingSeen,
      }}
    >
      {children}
    </OnboardingSettingsContext.Provider>
  );
};

export const useOnboardingSettings = () =>
  useContext(OnboardingSettingsContext);
